import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Check, ChevronDown, ChevronUp, Send, SmilePlus, Trash2, X } from 'lucide-react'
import { api, upload } from '../../lib/api.js'
import { useOnline } from '../../lib/useOnline.js'
import { fmtDate } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { BOX_NAME, BOX_DESC } from '../../lib/suggestionBox.js'
import SwipeRow from '../../components/SwipeRow.jsx'
import Carousel from '../../components/Carousel.jsx'
import Initials from '../sessionlog/Initials.jsx'

/**
 * The suggestion box — the hub's fourth app (Charles, 2026-08-06).
 * Anyone signed in posts under their name (guests very much included);
 * everyone reads; delete your own, admins delete any. Admins hand down a
 * verdict — Solved / Discussing get a quiet chip; Rejected! gets the
 * rubber stamp, diagonally, in uneven ink. Each suggestion carries its
 * own back-and-forth comment thread.
 */

// Matches the server's enum — a short fixed palette, not a picker.
const REACTIONS = ['👍', '❤️', '🎉', '😂', '👀']

const VERDICTS = [
  { key: 'solved', label: 'Solved', bg: '#DDEDEA', fg: '#2F6B5E' },
  // Label only — the stored key stays 'considering', so verdicts handed
  // down before the rename keep their chip (Charles, 2026-08-27).
  { key: 'considering', label: 'Discussing', bg: '#DDEBF9', fg: '#2B5E9E' },
  { key: 'rejected', label: 'Rejected!', bg: '#FBE4E4', fg: '#A13B3B' },
]
const verdictOf = (key) => VERDICTS.find((v) => v.key === key)

// Uneven ink (Charles, 2026-08-06: "like a real stamp") — SVG fractal
// noise as an alpha mask eats little gaps out of the print.
const INK_MASK = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='320' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.55' numOctaves='2' seed='7'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 -1.6 1.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`

/** The REJECTED! rubber stamp — slapped diagonally across the suggestion
 *  (only the suggestion; the comment thread below stays unstamped). */
function RejectedStamp() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
      <span
        className="-rotate-12 whitespace-nowrap rounded-md px-4 py-1 font-black uppercase mix-blend-multiply"
        style={{
          color: '#C42B1E',
          border: '4px double #C42B1E',
          fontSize: '1.5rem',
          letterSpacing: '4px',
          opacity: 0.6,
          textShadow: '0 0 1px #C42B1E',
          WebkitMaskImage: INK_MASK,
          maskImage: INK_MASK,
          WebkitMaskSize: 'cover',
          maskSize: 'cover',
        }}
      >
        Rejected!
      </span>
    </span>
  )
}

export default function SuggestionBoxPage() {
  const qc = useQueryClient()
  const online = useOnline()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({ queryKey: ['box'], queryFn: () => api('/box') })
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  // Photos picked for the post in progress — the file input's capture-free
  // accept="image/*" gives iOS its Take Photo / Photo Library sheet.
  const [picked, setPicked] = useState([]) // [{file, url: objectURL}]
  const fileRef = useRef(null)
  // The photo open full-screen: {photos, idx} — one carousel for the page.
  const [viewer, setViewer] = useState(null)
  // Solved suggestions collapse to one line under their own divider
  // (Charles, 2026-08-07) — this tracks the ones tapped back open.
  const [expanded, setExpanded] = useState(() => new Set())

  // The server flags what's new for you against your last visit — and that
  // same GET marks the visit, so a refetch (after you reply, say) comes back
  // unflagged. Remember every flagged id for this mount so the New markers
  // don't vanish mid-visit; leaving and returning starts clean.
  const fresh = useRef({ comments: new Set(), verdicts: new Set() })
  for (const item of data?.items || []) {
    if (item.verdictNew) fresh.current.verdicts.add(item.id)
    for (const c of item.comments) if (c.isNew) fresh.current.comments.add(c.id)
  }

  // Opening the box read it (the GET bumped seen server-side) — refetch the
  // activity count so the hub-tile and rail dots clear.
  useEffect(() => {
    if (data) qc.invalidateQueries({ queryKey: ['box-activity'] })
  }, [data, qc])

  const isAdmin = me.data?.user?.role === 'admin'
  const canPost = Boolean(me.data?.user) && online

  function pickFiles(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return
    setPicked((p) => [...p, ...files.map((file) => ({ file, url: URL.createObjectURL(file) }))])
  }

  function unpick(url) {
    URL.revokeObjectURL(url)
    setPicked((p) => p.filter((x) => x.url !== url))
  }

  async function post() {
    const t = text.trim()
    if (!t || busy) return
    setBusy(true)
    try {
      const created = await api('/box', { body: { text: t } })
      // Photos ride behind the post — a failed upload keeps the suggestion
      // and says which photo didn't make it.
      for (const p of picked) {
        try {
          await upload(`/box/${created.id}/media`, p.file)
        } catch (err) {
          toast(`Photo "${p.file.name}" didn’t upload: ${err.message}`, 'error')
        }
        URL.revokeObjectURL(p.url)
      }
      setText('')
      setPicked([])
      qc.invalidateQueries({ queryKey: ['box'] })
    } catch (err) {
      toast(err.message || 'Couldn’t post', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function removePhoto(item, photo) {
    if (!window.confirm('Remove this photo?')) return
    try {
      await api(`/box/${item.id}/media/${photo.id}`, { method: 'DELETE' })
      qc.setQueryData(['box'], (d) => ({
        ...d,
        items: d.items.map((x) =>
          x.id === item.id ? { ...x, photos: x.photos.filter((p) => p.id !== photo.id) } : x
        ),
      }))
    } catch (err) {
      toast(err.message || 'Couldn’t remove photo', 'error')
    }
  }

  async function remove(item) {
    if (!window.confirm('Delete this suggestion and its comments?')) return
    try {
      await api(`/box/${item.id}`, { method: 'DELETE' })
      qc.setQueryData(['box'], (d) => ({ ...d, items: d.items.filter((x) => x.id !== item.id) }))
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  // Tap a verdict to set it; tap the active one again to clear it.
  async function setVerdict(item, key) {
    const status = item.status === key ? '' : key
    qc.setQueryData(['box'], (d) => ({
      ...d,
      items: d.items.map((x) => (x.id === item.id ? { ...x, status } : x)),
    }))
    try {
      await api(`/box/${item.id}`, { method: 'PATCH', body: { status } })
    } catch (err) {
      toast(err.message || 'Couldn’t save', 'error')
      qc.invalidateQueries({ queryKey: ['box'] })
    }
  }

  // Reaction cache writes — one for the suggestion itself, one for a
  // comment inside it. Both replace just the `reactions` array in place.
  const setItemReactions = (itemId, reactions) =>
    qc.setQueryData(['box'], (d) => ({
      ...d,
      items: d.items.map((x) => (x.id === itemId ? { ...x, reactions } : x)),
    }))

  const setCommentReactions = (itemId, commentId, reactions) =>
    qc.setQueryData(['box'], (d) => ({
      ...d,
      items: d.items.map((x) =>
        x.id === itemId
          ? { ...x, comments: x.comments.map((c) => (c.id === commentId ? { ...c, reactions } : c)) }
          : x
      ),
    }))

  async function removeComment(item, comment) {
    try {
      await api(`/box/comments/${comment.id}`, { method: 'DELETE' })
      qc.setQueryData(['box'], (d) => ({
        ...d,
        items: d.items.map((x) =>
          x.id === item.id ? { ...x, comments: x.comments.filter((c) => c.id !== comment.id) } : x
        ),
      }))
    } catch (err) {
      toast(err.message || 'Couldn’t delete comment', 'error')
    }
  }

  const items = data?.items || []
  const open = items.filter((i) => i.status !== 'solved')
  const solved = items.filter((i) => i.status === 'solved')

  function toggleSolved(id) {
    setExpanded((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // A collapsed row still has to show its New markers, or news hides itself.
  const hasNews = (item) =>
    fresh.current.verdicts.has(item.id) || item.comments.some((c) => fresh.current.comments.has(c.id))

  return (
    <div>
      <div className="mb-4 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">{BOX_NAME}</h1>
        <p className="mt-1 text-sm text-muted">{BOX_DESC}</p>
      </div>

      {canPost && (
        <div className="card mb-5 p-3">
          <textarea
            rows={3}
            placeholder="Speak your mind…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full resize-y rounded-lg border-0 bg-grouped px-3 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
          />
          {picked.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {picked.map((p) => (
                <div key={p.url} className="relative">
                  <img src={p.url} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  <button
                    onClick={() => unpick(p.url)}
                    aria-label="Remove photo"
                    className="absolute -right-1.5 -top-1.5 rounded-full bg-ink p-0.5 text-white"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between">
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={pickFiles} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              aria-label="Add a photo"
              className="tap flex items-center gap-1.5 rounded-xl px-2 py-2 text-sm font-medium text-muted"
            >
              <Camera size={17} /> Photo
            </button>
            <button
              onClick={post}
              disabled={busy || !text.trim()}
              className="tap flex items-center gap-1.5 rounded-xl bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              <Send size={14} /> {busy ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
      {!online && <p className="mb-5 text-sm text-muted">The box needs a connection to post.</p>}

      {isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted">Nothing in the box yet. Be the first to complain.</p>
      ) : (
        <>
          {open.length > 0 && (
            <div className="card divide-y divide-hairline overflow-hidden">{open.map((item) => renderCard(item))}</div>
          )}

          {solved.length > 0 && (
            <>
              <div className={'mb-2 flex items-center gap-3 ' + (open.length > 0 ? 'mt-6' : '')}>
                <span className="text-xs font-semibold uppercase tracking-wide text-faint">
                  Solved · {solved.length}
                </span>
                <span className="h-px flex-1 bg-hairline" />
              </div>
              <div className="card divide-y divide-hairline overflow-hidden">
                {solved.map((item) =>
                  expanded.has(item.id) ? (
                    renderCard(item, true)
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => toggleSolved(item.id)}
                      className="tap flex w-full items-center gap-2 bg-white px-4 py-2.5 text-left"
                    >
                      <Check size={14} className="shrink-0" style={{ color: verdictOf('solved').fg }} />
                      <span className="min-w-0 flex-1 truncate text-[14px] text-muted">{item.text}</span>
                      {(item.photos?.length ?? 0) > 0 && <Camera size={13} className="shrink-0 text-faint" />}
                      {hasNews(item) && <NewPill />}
                      <span className="shrink-0 text-[11px] text-faint">{fmtDate(item.createdAt)}</span>
                      <ChevronDown size={14} className="shrink-0 text-faint" />
                    </button>
                  )
                )}
              </div>
            </>
          )}
        </>
      )}

      {viewer !== null && (
        <Carousel photos={viewer.photos} start={viewer.idx} onClose={() => setViewer(null)} />
      )}
    </div>
  )

  /** One full suggestion card; `collapsible` adds the fold-away chevron
   *  (solved section only — open suggestions always show in full). */
  function renderCard(item, collapsible = false) {
    return (
      <MaybeSwipe key={item.id} enabled={item.canDelete} onDelete={() => remove(item)}>
        <div className="bg-white px-4 py-3">
                {/* the stamped zone: suggestion + byline only */}
                <div className="relative">
                  {item.status === 'rejected' && <RejectedStamp />}
                  {collapsible && (
                    <button
                      onClick={() => toggleSolved(item.id)}
                      title="Collapse"
                      className="absolute -right-1 -top-1 z-20 rounded p-1 text-faint"
                    >
                      <ChevronUp size={14} />
                    </button>
                  )}
                  <p
                    className={
                      'whitespace-pre-wrap text-[15px] leading-relaxed text-ink' + (collapsible ? ' pr-7' : '')
                    }
                  >
                    {item.text}
                  </p>
                  {(item.photos?.length ?? 0) > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {item.photos.map((p, i) => (
                        <div key={p.id} className="relative">
                          <button onClick={() => setViewer({ photos: item.photos, idx: i })} className="tap block">
                            <img src={p.thumb} alt="" className="h-20 w-20 rounded-lg object-cover" />
                          </button>
                          {item.canDelete && (
                            <button
                              onClick={() => removePhoto(item, p)}
                              aria-label="Remove photo"
                              className="absolute -right-1.5 -top-1.5 rounded-full bg-ink p-0.5 text-white"
                            >
                              <X size={11} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-faint">
                    <Initials name={item.by} />
                    <span>{item.by}</span>
                    <span>· {fmtDate(item.createdAt)}</span>
                    {fresh.current.verdicts.has(item.id) && <NewPill />}
                    {item.status && item.status !== 'rejected' && !isAdmin && (
                      <span
                        className="rounded px-2 py-0.5 text-[11px] font-semibold"
                        style={{ backgroundColor: verdictOf(item.status)?.bg, color: verdictOf(item.status)?.fg }}
                      >
                        {verdictOf(item.status)?.label}
                      </span>
                    )}
                    {item.canDelete && (
                      <button
                        onClick={() => remove(item)}
                        title="Delete suggestion"
                        className="ml-auto rounded p-1 text-faint hover:text-danger active:text-danger"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                  <Reactions
                    path={`/box/${item.id}/reactions`}
                    reactions={item.reactions}
                    canPost={canPost}
                    onChange={(r) => setItemReactions(item.id, r)}
                  />
                </div>

                {/* the admin bench */}
                {isAdmin && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {VERDICTS.map((v) => {
                      const active = item.status === v.key
                      return (
                        <button
                          key={v.key}
                          onClick={() => setVerdict(item, v.key)}
                          className="rounded px-2 py-1 text-[11px] font-semibold"
                          style={
                            active
                              ? { backgroundColor: v.bg, color: v.fg }
                              : { color: 'var(--color-faint)', border: '1px dashed var(--color-hairline)' }
                          }
                        >
                          {v.label}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/* the back-and-forth */}
                {(item.comments.length > 0 || canPost) && (
                  <div className="mt-3 space-y-2 border-t border-hairline pt-2.5">
                    {item.comments.map((c) => (
                      <div key={c.id} className="flex items-start gap-2 text-[13px]">
                        <Initials name={c.by} />
                        <div className="min-w-0 flex-1 leading-snug">
                          <span className="font-medium text-ink">{c.by}</span>{' '}
                          <span className="whitespace-pre-wrap text-ink">{c.text}</span>{' '}
                          <span className="whitespace-nowrap text-[11px] text-faint">{fmtDate(c.createdAt)}</span>
                          {fresh.current.comments.has(c.id) && <NewPill />}
                          <Reactions
                            path={`/box/comments/${c.id}/reactions`}
                            reactions={c.reactions}
                            canPost={canPost}
                            compact
                            onChange={(r) => setCommentReactions(item.id, c.id, r)}
                          />
                        </div>
                        {c.canDelete && (
                          <button
                            onClick={() => removeComment(item, c)}
                            title="Delete comment"
                            className="rounded p-0.5 text-faint hover:text-danger active:text-danger"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    {canPost && <Reply itemId={item.id} />}
                  </div>
                )}
        </div>
      </MaybeSwipe>
    )
  }
}

/**
 * The reaction strip under a suggestion or a comment. Shows only the emoji
 * people actually used, plus a quiet + that opens the palette; tapping one
 * you already gave takes it back. Optimistic — a reaction that fails to
 * save snaps back rather than lying.
 */
function Reactions({ path, reactions, canPost, onChange, compact = false }) {
  const [at, setAt] = useState(null) // palette screen position, null = closed
  const btnRef = useRef(null)
  const used = reactions || []
  const open = at !== null

  // Portaled to <body> and positioned in screen coords: the cards sit inside
  // `overflow-hidden` wrappers (SwipeRow), which would clip a popover opening
  // upward out of a short card — the same trap the lightbox and carousel hit.
  function openPalette() {
    const r = btnRef.current?.getBoundingClientRect()
    if (!r) return
    const PALETTE_H = 58 // measured; a low estimate makes it overlap the button
    const above = r.top - PALETTE_H - 6
    setAt(above > 8 ? { top: above, left: r.left } : { top: r.bottom + 6, left: r.left })
  }

  async function toggle(emoji) {
    setAt(null)
    const before = used
    const has = before.find((r) => r.emoji === emoji)?.mine
    // optimistic: bump or drop, and drop the bucket entirely at zero
    onChange(
      (has
        ? before.map((r) => (r.emoji === emoji ? { ...r, count: r.count - 1, mine: false } : r))
        : before.some((r) => r.emoji === emoji)
          ? before.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, mine: true } : r))
          : [...before, { emoji, count: 1, mine: true, who: [] }]
      ).filter((r) => r.count > 0)
    )
    try {
      await api(path, { body: { emoji } })
    } catch (err) {
      onChange(before)
      toast(err.message || 'Couldn’t react', 'error')
    }
  }

  if (used.length === 0 && !canPost) return null
  return (
    <div className={(compact ? 'mt-1 ' : 'mt-2 ') + 'flex flex-wrap items-center gap-1.5'}>
      {used.map((r) => (
        <button
          key={r.emoji}
          onClick={() => canPost && toggle(r.emoji)}
          disabled={!canPost}
          title={r.who?.join(', ')}
          className={
            'tap flex items-center gap-1 rounded-full px-2 py-0.5 text-[13px] leading-5 ' +
            (r.mine ? 'border border-navy bg-white text-navy' : 'border border-hairline bg-grouped text-muted')
          }
        >
          <span>{r.emoji}</span>
          <span className="text-[11px] font-semibold tabular-nums">{r.count}</span>
        </button>
      ))}
      {canPost && (
        <button
          ref={btnRef}
          onClick={() => (open ? setAt(null) : openPalette())}
          aria-label="Add a reaction"
          className={
            'tap flex items-center rounded-full text-faint ' +
            (compact ? 'p-0.5' : 'border border-hairline px-2 py-1')
          }
        >
          <SmilePlus size={compact ? 13 : 14} />
        </button>
      )}
      {open &&
        createPortal(
          <>
            {/* tap-away closes; sits under the palette, over everything else */}
            <button
              aria-hidden
              tabIndex={-1}
              onClick={() => setAt(null)}
              className="fixed inset-0 z-40 cursor-default"
            />
            <div
              className="fixed z-50 flex gap-1 rounded-xl border border-hairline bg-white px-2 py-1.5 shadow-lg"
              style={{ top: at.top, left: at.left }}
            >
              {REACTIONS.map((e) => (
                <button
                  key={e}
                  onClick={() => toggle(e)}
                  className="tap rounded-lg px-1.5 py-0.5 text-[19px] leading-6 active:bg-grouped"
                >
                  {e}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

/** What the dot pointed at — navy (news, not alarm), gone next visit. */
function NewPill() {
  return (
    <span className="ml-1.5 inline-block rounded bg-navy px-1.5 py-px align-middle text-[10px] font-semibold uppercase tracking-wide text-white">
      New
    </span>
  )
}

/** The chat line at the bottom of each thread — Enter or the arrow sends. */
function Reply({ itemId }) {
  const qc = useQueryClient()
  const [val, setVal] = useState('')
  const [sending, setSending] = useState(false)

  async function send() {
    const t = val.trim()
    if (!t || sending) return
    setSending(true)
    try {
      await api(`/box/${itemId}/comments`, { body: { text: t } })
      setVal('')
      qc.invalidateQueries({ queryKey: ['box'] })
    } catch (err) {
      toast(err.message || 'Couldn’t reply', 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="text"
        placeholder="Reply…"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') send()
        }}
        className="min-w-0 flex-1 rounded-lg border-0 bg-grouped px-3 py-1.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-1 focus:ring-link"
      />
      <button
        onClick={send}
        disabled={sending || !val.trim()}
        aria-label="Send reply"
        className="rounded-lg p-1.5 text-muted disabled:opacity-40"
      >
        <Send size={14} />
      </button>
    </div>
  )
}

/** Swipe-left-to-delete when allowed; plain row otherwise. */
function MaybeSwipe({ enabled, onDelete, children }) {
  if (!enabled) return children
  return <SwipeRow onDelete={onDelete}>{children}</SwipeRow>
}
